import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  VisibilityState,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowSelectionState,
  SortingState,
  TableMeta,
  TableState,
  Updater,
  useReactTable,
} from '@tanstack/react-table'
import classnames from 'classnames'
import React, {
  Dispatch,
  forwardRef,
  Ref,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Table, TableCell, TableHeading, Button, Text } from '@nike/eds'
import { Filter } from "@nike/nike-design-system-icons";


import {
  ColumnFilters,
  exactStringFilter,
  GlobalFilter,
  PaginationControls,
  SortableHeader,
  ColumnFiltersSidePanel,
  ColumnFiltersBadge
} from './table-components'
import { includesStringFilter } from './table-components/ColumnFilters'
import './table.css'

type ExtendedColumnDef<T> = ColumnDef<T, any> & {
  filterType?: 'text' | 'select' | 'multiselect' | 'date'
  filterOptions?: Array<{ value: string; label: string }>
  getFilterOptions?: (tableState: any) => Array<{ value: string; label: string }>
  filterGroup?: string
}

/**
 * Controlled state overrides. When provided, these values take precedence
 * over the corresponding internal state. Omit a field to let the table
 * manage it internally (uncontrolled).
 */
type ControlledTableState = {
  globalFilter?: string
  sorting?: SortingState
  pagination?: PaginationState
  columnFilters?: ColumnFiltersState
  expanded?: ExpandedState
  rowSelection?: RowSelectionState
  columnOrder?: ColumnOrderState
  columnVisibility?: VisibilityState
  columnFiltersSidePanelIsOpen?: boolean
}

type TableProps<T> = {
  columns: ExtendedColumnDef<T>[]
  data: T[]
  meta?: TableMeta<T>

  // Sorting
  enableSorting?: boolean
  manualSorting?: boolean
  initialSort?: SortingState
  onSortChange?: (sorting: SortingState) => void

  // Layout
  tableTitle?: React.ReactNode
  stickyColumnIndex?: number
  disableStickyHeader?: boolean
  className?: string
  isNested?: boolean
  style?: React.CSSProperties

  // Expanded rows
  enableExpanding?: boolean
  initialExpanded?: ExpandedState
  onExpandedChange?: (expanded: ExpandedState) => void
  renderExpandedContent?: (row: T) => React.ReactNode

  // Pagination
  enablePagination?: boolean
  manualPagination?: boolean
  pageIndex?: number
  pageSize?: number
  pageSizeOptions?: number[]
  totalCount?: number
  autoResetPageIndex?: boolean
  onPaginationChange?: (pagination: PaginationState) => void

  // Filtering
  enableGlobalFilter?: boolean
  enableColumnFilters?: boolean
  enableColumnFiltersSidePanel?: boolean
  enableGlobalFilterInSidePanel?: boolean
  manualFiltering?: boolean
  initialGlobalFilter?: string
  initialColumnFilters?: ColumnFiltersState
  globalFilterPlaceholder?: string
  globalFilterLiveSearch?: boolean
  onColumnFiltersChange?: (columnFilters: ColumnFiltersState) => void
  onGlobalFilterChange?: (globalFilter: string) => void
  onColumnFiltersSidePanelOpenChange?: (isOpen: boolean) => void

  // Row selection
  enableRowSelection?: boolean
  enableMultiRowSelection?: boolean
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void
  getRowId?: (row: T) => string

  // Column order
  onColumnOrderChange?: (columnOrder: ColumnOrderState) => void

  // Display
  noDataText?: string
  noDataContent?: React.ReactNode

  // Controlled state (overrides internal state when provided)
  state?: ControlledTableState
}

export type TableRef = {
  getTableState: () => TableState
  resetPageIndex: () => void
  setPagination: Dispatch<SetStateAction<PaginationState>>
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>
  setSorting: Dispatch<SetStateAction<SortingState>>
  setExpanded: Dispatch<SetStateAction<ExpandedState>>
  getAllColumns: () => Column<any>[]
}

const hasChildren = <T,>(row: any): row is { children: T[] } => {
  return !!row.children?.length
}

export default forwardRef(function DataTable<T>(
  {
    columns,
    data,
    meta,
    enableSorting = false,
    manualSorting = false,
    enablePagination = false,
    manualPagination = false,
    enableGlobalFilter = false,
    enableColumnFilters = false,
    enableExpanding = false,
    manualFiltering = false,
    disableStickyHeader = false,
    enableRowSelection = false,
    enableMultiRowSelection = true,
    enableColumnFiltersSidePanel = false,
    enableGlobalFilterInSidePanel = true,
    initialColumnFilters = [],
    initialSort = [],
    initialExpanded = {},
    initialGlobalFilter = '',
    pageIndex = 0,
    pageSize = 10,
    pageSizeOptions = [10, 25, 50, 100],
    totalCount = 0,
    autoResetPageIndex = false,
    noDataText = 'No items found.',
    globalFilterPlaceholder = 'Search...',
    globalFilterLiveSearch = true,
    stickyColumnIndex,
    onColumnFiltersChange,
    onGlobalFilterChange,
    onPaginationChange,
    onSortChange,
    onRowSelectionChange,
    onColumnOrderChange,
    onColumnFiltersSidePanelOpenChange,
    onExpandedChange,
    renderExpandedContent,
    getRowId,
    tableTitle = null,
    noDataContent = null,
    className = '',
    isNested = false,
    style,
    state: controlledState,
  }: TableProps<T>,
  ref: Ref<TableRef>
) {
  const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter)
  const [sorting, setSorting] = useState<SortingState>(initialSort)
  const [expanded, setExpanded] = useState<ExpandedState>(initialExpanded)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex,
    pageSize,
  })
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    controlledState?.columnOrder || []
  )
  const [sidePanelOpen, setSidePanelOpen] = useState(false)

  const isSidePanelOpen = controlledState?.columnFiltersSidePanelIsOpen ?? sidePanelOpen

  const handleSidePanelOpenChange = (open: boolean) => {
    setSidePanelOpen(open)
    onColumnFiltersSidePanelOpenChange?.(open)
  }

  const handleGlobalFilterChange = (value: string) => {
    setGlobalFilter(value)
    onGlobalFilterChange?.(value)
  }

  const handleSortingChange = (updater: Updater<SortingState>) => {
    setSorting((prev: SortingState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onSortChange?.(next)
      return next
    })
  }

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    setPagination((prev: PaginationState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onPaginationChange?.(next)
      return next
    })
  }

  const handleColumnFiltersChange = (updater: Updater<ColumnFiltersState>) => {
    setColumnFilters((prev: ColumnFiltersState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onColumnFiltersChange?.(next)
      return next
    })
  }

  const handleRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    setRowSelection((prev: RowSelectionState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onRowSelectionChange?.(next)
      return next
    })
  }

  const handleColumnOrderChange = (updater: Updater<ColumnOrderState>) => {
    setColumnOrder((prev: ColumnOrderState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onColumnOrderChange?.(next)
      return next
    })
  }

  const handleExpandedChange = (updater: Updater<ExpandedState>) => {
    setExpanded((prev: ExpandedState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onExpandedChange?.(next)
      return next
    })
  }

  const processedColumns = useMemo(() => {
    return columns.map((column) => {
      if (column.filterType === 'select') {
        return {
          ...column,
          filterFn: column.filterFn || exactStringFilter,
        }
      }

      if (column.filterType === 'multiselect') {
        return {
          ...column,
          filterFn: column.filterFn || ((row: any, columnId: string, filterValue: string[]) => {
            if (!Array.isArray(filterValue) || filterValue.length === 0) return true
            const cellValue = row.getValue(columnId)
            return filterValue.includes(String(cellValue ?? ''))
          }),
        }
      }

      return {
        ...column,
        filterFn: column.filterFn || includesStringFilter,
      }
    })
  }, [columns])

  const resolvedState = {
    globalFilter: enableGlobalFilter ? (controlledState?.globalFilter ?? globalFilter) : undefined,
    sorting: enableSorting ? (controlledState?.sorting ?? sorting) : [],
    pagination: enablePagination ? (controlledState?.pagination ?? pagination) : undefined,
    columnFilters: (enableColumnFilters || enableColumnFiltersSidePanel)
      ? (controlledState?.columnFilters ?? columnFilters)
      : [],
    expanded: controlledState?.expanded ?? (enableExpanding ? expanded : {}),
    rowSelection: controlledState?.rowSelection ?? rowSelection ?? {},
    columnOrder: controlledState?.columnOrder ?? columnOrder ?? [],
    columnVisibility: controlledState?.columnVisibility ?? {},
  }

  const table = useReactTable({
    data,
    columns: processedColumns,
    meta,
    onSortingChange: handleSortingChange,
    onPaginationChange: handlePaginationChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onExpandedChange: handleExpandedChange,
    onRowSelectionChange: enableRowSelection ? handleRowSelectionChange : undefined,
    onColumnOrderChange: handleColumnOrderChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getFilteredRowModel:
      enableGlobalFilter || enableColumnFilters ? getFilteredRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row: T) => {
      return hasChildren<T>(row) ? row.children : undefined
    },
    getRowId: getRowId as any,
    autoResetPageIndex,
    manualSorting,
    manualPagination,
    manualFiltering,
    enableMultiRowSelection,
    rowCount: manualPagination ? totalCount : undefined,
    state: resolvedState,
  })

  useImperativeHandle(ref, () => ({
    getTableState: () => table.getState(),
    resetPageIndex: () =>
      setPagination({
        ...pagination,
        pageIndex: 0,
      }),
    setPagination,
    setColumnFilters,
    setSorting,
    setExpanded,
    getAllColumns() {
      return table.getAllColumns();
    },
  }))

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const theadRef = useRef<HTMLTableSectionElement>(null)

  useEffect(() => {
    const thead = theadRef.current
    const container = scrollContainerRef.current
    if (!thead || !container) return

    const observer = new ResizeObserver(() => {
      const height = thead.getBoundingClientRect().height
      container.style.setProperty('--own-thead-height', `${height}px`)
      if (!isNested) {
        container.style.setProperty('--thead-height', `${height}px`)
      }
    })
    observer.observe(thead)
    return () => observer.disconnect()
  }, [isNested])

  const rows = table.getRowModel().rows
  const showRowHover = enableRowSelection;

  const tableState = table.getState();

  return (
    <div className="tableWrapper">

      {enableColumnFiltersSidePanel && (
        <ColumnFiltersSidePanel
          isOpen={isSidePanelOpen}
          table={table}
          columns={table.getAllColumns() as any || []}
          columnFilters={tableState?.columnFilters}
          handleColumnFiltersChange={handleColumnFiltersChange}
          onClose={() => handleSidePanelOpenChange(false)}
          globalFilter={enableGlobalFilter && enableGlobalFilterInSidePanel ? (controlledState?.globalFilter ?? globalFilter) : undefined}
          onGlobalFilterChange={enableGlobalFilter && enableGlobalFilterInSidePanel ? (val: string) => handleGlobalFilterChange(val) : undefined}
          globalFilterPlaceholder={globalFilterPlaceholder}
        />
      )}
      {enableGlobalFilter || enableColumnFiltersSidePanel || tableTitle ? (
        <div className="eds-flex eds-flex--m-justify-content-flex-end eds-flex--align-items-center eds-gap--32 eds-spacing--mb-16" >
          <div className="eds-flex--grow-1">
            {tableTitle}
          </div>
          {enableGlobalFilter && !enableColumnFiltersSidePanel && (
            <div className='globalFilterContainer'>
            <GlobalFilter
              globalFilter={resolvedState.globalFilter || ''}
              handleGlobalFilterChange={handleGlobalFilterChange}
              placeholder={globalFilterPlaceholder}
              liveSearch={globalFilterLiveSearch}
            />
            </div>
          )}
          {enableColumnFiltersSidePanel && (
            <div style={{ position: "relative" }}>
              <Button
                beforeSlot={<Filter />}
                size='small'
                onClick={() => handleSidePanelOpenChange(true)}
                style={{ position: "relative" }}
              >
                Filters
              </Button>
              <ColumnFiltersBadge
                count={(tableState?.columnFilters?.length || 0) + (enableGlobalFilter && enableGlobalFilterInSidePanel && resolvedState.globalFilter ? 1 : 0)}
              />
            </div>
          )}
        </div>) : null}
      <div
        ref={scrollContainerRef}
        className={classnames(
          'tableContainer',
          'overflowAuto',
          { 'rowHover': showRowHover },
          { 'stickyHeaderDisabled': disableStickyHeader },
          { 'hasPagination': enablePagination}
        )}
         style={style}
      >
        <Table className={classnames(className, {'isNested': isNested})} isFullWidth>
          <thead ref={theadRef} className='tableHeader'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <TableHeading
                    className={classnames(`table--header__${header.id}`, {
                      stickyColumnHeader: index === stickyColumnIndex,
                    })}
                    style={{
                      minWidth: header.getSize(),
                    }}
                    key={`${header.id}.${header.column.columnDef.header}`}
                  >
                    <div className='headerCellLabel'>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {enableSorting && (
                        <SortableHeader
                          getCanSort={header.column.getCanSort}
                          // @ts-ignore
                          getToggleSortingHandler={header.column.getToggleSortingHandler}
                          getIsSorted={header.column.getIsSorted}
                        />
                      )}
                    </div>

                    {enableColumnFilters && header.column.getCanFilter() ? (
                      <ColumnFilters
                        enableColumnFilters={enableColumnFilters}
                        header={header as any}
                      />
                    ) : null}
                  </TableHeading>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row) => {
                return (
                  <React.Fragment key={row.id}>
                  <tr
                    className={enableRowSelection && row.getIsSelected() ? 'selected' : undefined}
                    onClick={enableRowSelection ? row.getToggleSelectedHandler() : undefined}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell
                        className={classnames({
                          'stickyColumnBody': index === stickyColumnIndex,
                          'expandableRowCell': row.getCanExpand(),
                        })}
                        key={`${cell.id}.${cell.column.columnDef.header}`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                  <tr
                    className='expandedRow'
                    key={`${row.id}-expanded`}
                  >
                    <td colSpan={row.getAllCells().length}>
                      <div className={'expandedContentWrapper'}>
                      {typeof renderExpandedContent === 'function'
                        ? renderExpandedContent(row.original)
                        : <div></div>}
                      </div>
                    </td>
                  </tr>
                  )}
                  </React.Fragment>
                )
              })
            ) : (
              <tr>
                <TableCell colSpan={table.getAllColumns().length}>
                  {noDataContent || (
                    <div className='noDataText'>
                      <Text font="title-4" as="h4" className="eds-spacing--mt-56 eds-spacing--mb-56" >{noDataText}</Text>
                    </div>
                  )}
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {enablePagination && <PaginationControls table={table} pageSizeOptions={pageSizeOptions} totalCount={totalCount} />}
    </div>
  )
})
